import React from 'react'

import {flatMap} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import {useNavigate} from 'react-router'

import {useAppDispatch} from '../../hooks/redux'
import {fatalError} from '../../redux/features/core'

import useAsync from '../../../../commons/src/hooks/use-async'
import * as optionApi from '../../services/api/option'
import * as providerInfoApi from '../../services/api/provider-info'
import {goToEditProviderInfo} from '../../services/navigation'
import {MixedValue, isMixedValue, toMixedValidationSchema, toTouchedFormData} from '../utils/form'

import {
  EditProviderInfoTab,
  OptionId,
  ProviderId,
  ProviderInfo,
  ProviderInfoId,
  ProviderInfoOption,
} from '../../types'

import SaveButtonPartial from '../save-button-partial'

import PrintingMethodFormPartial, {
  MixedFormData,
  SingleFormData,
  formDataToPartialRequest,
  formatInfillName,
  formatToleranceName,
  initialValues,
  printingMethodToMixedFormData,
  sortInfills,
  sortTolerances,
  updateLinkedOptions,
  validationSchema,
} from './printing-method-form-partial'

type Props = {
  providerId: ProviderId
  providerInfos: Array<ProviderInfo>
}

const EditPrintingMethodPartial = ({providerId, providerInfos}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const {
    isLoading: isInfillsLoading,
    data: infills = [],
    refresh: refreshInfills,
  } = useAsync(() => optionApi.getInfillsWithLinkedProviderInfosByProvider(providerId), [], {
    onError: error => dispatch(fatalError({error})),
  })

  const {
    isLoading: isTolerancesLoading,
    data: tolerances = [],
    refresh: refreshTolerances,
  } = useAsync(() => optionApi.getTolerancesWithLinkedProviderInfosByProvider(providerId), [], {
    onError: error => dispatch(fatalError({error})),
  })

  const infillOptions = sortInfills(infills).map(infill => ({
    label: formatInfillName(infill),
    value: infill.id,
  }))

  const toleranceOptions = sortTolerances(tolerances).map(tolerance => ({
    label: formatToleranceName(tolerance),
    value: tolerance.id,
  }))

  const updateOptions = async (
    key: 'infillIds' | 'toleranceIds',
    touchedValues: Partial<SingleFormData>,
    update: (
      initialOptionIds: OptionId[],
      nextOptionIds: OptionId[],
      providerInfoIds: ProviderInfoId[]
    ) => Promise<ProviderInfoOption[]>,
    onDone: () => void
  ) => {
    const providerInfoIds = providerInfos.map(providerInfo => providerInfo.id)
    // Treat cases differently where infills/tolerances were initially a mixed value
    // in order to be able to find out their correct initial value.
    if (!isMixedValue(formik.initialValues[key])) {
      const initialOptionIds = formik.initialValues[key]

      await update(initialOptionIds, touchedValues[key] as OptionId[], providerInfoIds)
    } else {
      const updateLinkedOptionPromises = flatMap(
        providerInfos,
        async (providerInfo: ProviderInfo, index: number) => {
          const initialOptionIds = (formik.initialValues[key] as MixedValue<OptionId[]>).values[
            index
          ]

          return update(initialOptionIds, touchedValues[key] as OptionId[], [providerInfo.id])
        }
      )

      await Promise.all(updateLinkedOptionPromises)
    }

    onDone()
  }

  const formik = useFormik<MixedFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...printingMethodToMixedFormData(
        providerInfos,
        sortInfills(infills),
        sortTolerances(tolerances)
      ),
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(values, initialValues) as Partial<SingleFormData>
      const providerInfoRequest = formDataToPartialRequest(touchedValues)

      try {
        if (touchedValues.infillIds) {
          await updateOptions('infillIds', touchedValues, updateLinkedOptions, refreshInfills)
        }
        if (touchedValues.toleranceIds) {
          await updateOptions('toleranceIds', touchedValues, updateLinkedOptions, refreshTolerances)
        }

        const nextProviderInfos = await Promise.all(
          providerInfos.map(providerInfo =>
            providerInfoApi.patch(
              {
                ...providerInfoRequest,
                // If we change the provider info the API expects the materialConfigId otherwise we get an error.
                materialConfigId: providerInfo.materialConfigId,
              },
              providerInfo.id
            )
          )
        )

        goToEditProviderInfo(navigate, {
          providerInfos: nextProviderInfos,
          activeTab: EditProviderInfoTab.PRINTING_METHOD,
          replace: true,
        })

        setIsSubmitted(true)
      } catch (error) {
        if ((error as Error).message === 'Unallowed Relation Expression') {
          actions.setFieldError(
            'formikGeneral',
            'Infill options are only supported for FDM configurations and Tolerance options are only supported for CNC configurations.'
          )
        } else {
          actions.setFieldError('formikGeneral', (error as Error).message)
        }
      }
    },
  })

  return (
    <PrintingMethodFormPartial
      formik={formik}
      isLoading={isInfillsLoading || isTolerancesLoading}
      onRefreshInfills={refreshInfills}
      onRefreshTolerances={refreshTolerances}
      submitButton={<SaveButtonPartial formik={formik} isSubmitted={isSubmitted} />}
      infillOptions={infillOptions}
      toleranceOptions={toleranceOptions}
      providerInfos={providerInfos}
    />
  )
}

export default EditPrintingMethodPartial
